import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { SafeUrl } from '@angular/platform-browser';
import { PageSize } from 'src/app/models/document.model';
import { ViewerDocumentModel } from 'src/app/models/viewer/viewer-document-model.model';

interface PointerPosition {
  x: number;
  y: number;
}

/**
 * Minimal contract for the lazy-loading page logic reused from the calling component
 * (e.g. @see PerformActionDocumentPdfComponent, which satisfies this structurally).
 * Kept as a local interface instead of importing the concrete component, to avoid a
 * circular dependency between this component and the service opening it.
 */
export interface PdfPageProvider {
  loadPage(document: ViewerDocumentModel, pageId: number): void;
  getPageUrl(pageId: number): SafeUrl | null | undefined;
  checkIfItemIsRendered(pageId: number): boolean;
}

const MIN_SCALE = 1;
const MAX_SCALE = 3.5;
const SWIPE_THRESHOLD_PX = 60;
/** Above this scale a single pointer drag is always treated as PAN, never as swipe-navigation. */
const PAN_SCALE_THRESHOLD = 1.02;
/** Max delay (ms) between two taps to still count as a double-tap. */
const DOUBLE_TAP_MAX_DELAY_MS = 300;
/** Max pointer movement (px, in content-relative space) to still count as a tap, not a drag/swipe. */
const DOUBLE_TAP_MAX_MOVEMENT_PX = 12;
/** Target scale when double-tapping to zoom in from the fit state. */
const DOUBLE_TAP_ZOOM_SCALE = 2.5;

/**
 * Standalone, full-screen page viewer for a single RegularPdf document page (mobile only).
 * Supports pinch-zoom, panning and swipe page navigation using pointer events.
 * Reuses the lazy-loading logic (loadPage/getPageUrl/checkIfItemIsRendered) of the
 * calling component instance (implementing @see PdfPageProvider) instead of duplicating it.
 */
@Component({
    templateUrl: './view-pdf-fullscreen.modal.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
    host: { class: 'view-pdf-fullscreen-modal' }
})
export class ViewPdfFullscreenModal implements OnInit {
  @Input('document') document!: ViewerDocumentModel;
  @Input('pageSizes') pageSizes: PageSize[] = [];
  @Input('initialPageId') initialPageId: number = 1;
  @Input('pdfComponent') pdfComponent!: PdfPageProvider;
  @Output('pageChanged') pageChangedEmitter = new EventEmitter<number>();

  @ViewChild('viewport') viewportRef!: ElementRef<HTMLElement>;

  currentPageId: number = 1;
  scale: number = MIN_SCALE;
  translateX: number = 0;
  translateY: number = 0;

  private pointers: Map<number, PointerPosition> = new Map();
  private isPinching: boolean = false;
  private pinchStartDistance: number = 0;
  private pinchStartScale: number = MIN_SCALE;
  private pinchStartMidpoint: PointerPosition = { x: 0, y: 0 };
  private pinchStartTranslate: PointerPosition = { x: 0, y: 0 };
  private panStart: PointerPosition = { x: 0, y: 0 };
  private panStartTranslate: PointerPosition = { x: 0, y: 0 };
  private swipeStartX: number = 0;
  private hadMultiplePointers: boolean = false;
  private lastTapTime: number = 0;
  private lastTapPoint: PointerPosition | null = null;

  constructor(public activeModal: NgbActiveModal) {
    // function cannot be empty
  }

  ngOnInit(): void {
    this.currentPageId = this.initialPageId;
    this.pageChangedEmitter.emit(this.currentPageId);
    this.loadPageBuffer(this.currentPageId);
  }

  get totalPages(): number {
    return this.pageSizes.length;
  }

  get aspectRatio(): string {
    let size = this.pageSizes[this.currentPageId - 1];
    return size ? `${size.X} / ${size.Y}` : '1 / 1';
  }

  isPageRendered(): boolean {
    return this.pdfComponent.checkIfItemIsRendered(this.currentPageId);
  }

  getCurrentPageUrl() {
    return this.pdfComponent.getPageUrl(this.currentPageId);
  }

  close(): void {
    this.activeModal.close();
  }

  /** Resets zoom/pan so the page fits the screen again. */
  fitToScreen(): void {
    this.scale = MIN_SCALE;
    this.translateX = 0;
    this.translateY = 0;
  }

  onPointerDown(event: PointerEvent): void {
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
    let point = this.toContentPoint(event);
    this.pointers.set(event.pointerId, point);

    if (this.pointers.size === 2) {
      let [p1, p2] = Array.from(this.pointers.values());
      this.isPinching = true;
      this.pinchStartDistance = this.distance(p1, p2);
      this.pinchStartScale = this.scale;
      this.pinchStartMidpoint = this.midpoint(p1, p2);
      this.pinchStartTranslate = { x: this.translateX, y: this.translateY };
      this.hadMultiplePointers = true;
    } else if (this.pointers.size === 1) {
      this.isPinching = false;
      this.hadMultiplePointers = false;
      this.panStart = point;
      this.panStartTranslate = { x: this.translateX, y: this.translateY };
      this.swipeStartX = point.x;
    }
  }

  onPointerMove(event: PointerEvent): void {
    if (!this.pointers.has(event.pointerId)) return;
    this.pointers.set(event.pointerId, this.toContentPoint(event));

    if (this.pointers.size === 2 && this.isPinching) {
      this.applyPinch();
      return;
    }

    if (this.pointers.size === 1 && !this.isPinching && this.scale > PAN_SCALE_THRESHOLD) {
      let point = this.pointers.get(event.pointerId)!;
      this.translateX = this.clampTranslate(this.panStartTranslate.x + (point.x - this.panStart.x), 'x');
      this.translateY = this.clampTranslate(this.panStartTranslate.y + (point.y - this.panStart.y), 'y');
    }
  }

  onPointerUp(event: PointerEvent): void {
    let wasSwipeCandidate = this.pointers.size === 1 && !this.isPinching && this.scale <= PAN_SCALE_THRESHOLD;
    let wasSinglePointerTapCandidate = this.pointers.size === 1 && !this.isPinching && !this.hadMultiplePointers;
    let lastPoint = this.pointers.get(event.pointerId);
    this.pointers.delete(event.pointerId);

    if (this.pointers.size === 1) {
      // pinch ended with one finger remaining: re-anchor panning to prevent a jump
      let remaining = Array.from(this.pointers.values())[0];
      this.panStart = { x: remaining.x, y: remaining.y };
      this.panStartTranslate = { x: this.translateX, y: this.translateY };
      this.isPinching = false;
    } else if (this.pointers.size === 0) {
      this.isPinching = false;
    }

    if (wasSwipeCandidate && lastPoint) {
      let deltaX = lastPoint.x - this.swipeStartX;
      if (Math.abs(deltaX) > SWIPE_THRESHOLD_PX) {
        this.goToPage(deltaX < 0 ? this.currentPageId + 1 : this.currentPageId - 1);
      }
    }

    if (wasSinglePointerTapCandidate && lastPoint && this.distance(this.panStart, lastPoint) <= DOUBLE_TAP_MAX_MOVEMENT_PX) {
      this.handleTap(lastPoint);
    }
  }

  private applyPinch(): void {
    let [p1, p2] = Array.from(this.pointers.values());
    if (this.pinchStartDistance === 0) return;

    let currentDistance = this.distance(p1, p2);
    let newScale = this.clampScale(this.pinchStartScale * (currentDistance / this.pinchStartDistance));
    let currentMidpoint = this.midpoint(p1, p2);
    let scaleRatio = newScale / this.pinchStartScale;

    // anchor-to-pointer: the content point under the pinch-start midpoint stays under the live midpoint
    let anchorX = this.pinchStartMidpoint.x - (this.pinchStartMidpoint.x - this.pinchStartTranslate.x) * scaleRatio;
    let anchorY = this.pinchStartMidpoint.y - (this.pinchStartMidpoint.y - this.pinchStartTranslate.y) * scaleRatio;
    let deltaMidpointX = currentMidpoint.x - this.pinchStartMidpoint.x;
    let deltaMidpointY = currentMidpoint.y - this.pinchStartMidpoint.y;

    this.scale = newScale;
    this.translateX = this.clampTranslate(anchorX + deltaMidpointX, 'x');
    this.translateY = this.clampTranslate(anchorY + deltaMidpointY, 'y');
  }

  private handleTap(point: PointerPosition): void {
    let now = Date.now();
    let isDoubleTap = this.lastTapPoint !== null
      && (now - this.lastTapTime) <= DOUBLE_TAP_MAX_DELAY_MS
      && this.distance(point, this.lastTapPoint) <= DOUBLE_TAP_MAX_MOVEMENT_PX;

    if (isDoubleTap) {
      this.lastTapTime = 0;
      this.lastTapPoint = null;
      this.toggleDoubleTapZoom(point);
    } else {
      this.lastTapTime = now;
      this.lastTapPoint = point;
    }
  }

  /** Zooms in around the tapped point when at fit scale, or resets to fit when already zoomed in. */
  private toggleDoubleTapZoom(point: PointerPosition): void {
    if (this.scale > PAN_SCALE_THRESHOLD) {
      this.fitToScreen();
      return;
    }
    let newScale = this.clampScale(DOUBLE_TAP_ZOOM_SCALE);
    let scaleRatio = newScale / this.scale;
    this.scale = newScale;
    this.translateX = this.clampTranslate(point.x - (point.x - this.translateX) * scaleRatio, 'x');
    this.translateY = this.clampTranslate(point.y - (point.y - this.translateY) * scaleRatio, 'y');
  }

  private goToPage(pageId: number): void {
    let clamped = Math.min(Math.max(pageId, 1), this.totalPages);
    this.currentPageId = clamped;
    this.pageChangedEmitter.emit(this.currentPageId);
    this.loadPageBuffer(this.currentPageId);
    this.fitToScreen();
  }

  private loadPageBuffer(pageId: number): void {
    this.pdfComponent.loadPage(this.document, pageId - 1);
    this.pdfComponent.loadPage(this.document, pageId);
    this.pdfComponent.loadPage(this.document, pageId + 1);
  }

  /**
   * Converts a pointer event's window-relative coordinates into coordinates relative to the
   * center of the `#viewport` container. `translateX`/`translateY` are applied to
   * `.fullscreen-viewer-page` (which is centered inside the viewport) via
   * `transform: translate(...) scale(...)` with `transform-origin: center center`, so a
   * translate of (0,0) corresponds to "page-center = viewport-center". All pinch/pan math
   * (midpoint, anchor, clamping) is expressed in that same viewport-center-relative space, so
   * pointer coordinates must be converted here before being used, instead of using raw
   * `clientX`/`clientY` (which are relative to the browser window).
   */
  private toContentPoint(event: PointerEvent): PointerPosition {
    let rect = this.viewportRef.nativeElement.getBoundingClientRect();
    return {
      x: event.clientX - rect.left - rect.width / 2,
      y: event.clientY - rect.top - rect.height / 2,
    };
  }

  private distance(a: PointerPosition, b: PointerPosition): number {
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  private midpoint(a: PointerPosition, b: PointerPosition): PointerPosition {
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  }

  private clampScale(scale: number): number {
    return Math.min(Math.max(scale, MIN_SCALE), MAX_SCALE);
  }

  /** Keeps the page from being panned too far outside of the viewport. */
  private clampTranslate(value: number, axis: 'x' | 'y'): number {
    let viewport = this.viewportRef?.nativeElement;
    if (!viewport) return value;
    let size = axis === 'x' ? viewport.clientWidth : viewport.clientHeight;
    let maxOffset = (size * (this.scale - 1)) / 2 + size * 0.15;
    return Math.min(Math.max(value, -maxOffset), maxOffset);
  }
}
